import React, { useState, useContext, useEffect } from 'react'
import { DashboardContext } from '../../../context/DashboardContext'
import {
  ChevronLeft,
  Apple,
  Tablet,
  Smartphone,
  Globe,
  Laptop,
  Computer,
  MonitorSmartphone,
  Package,
  Search,
} from 'lucide-react'
import { ProjectSmallIcon } from '../../icons/Project'

interface ChooseExistingProjectProps {
  handleChooseExistingProject: (project: any) => void
  onStageChange?: (stage: 'product' | 'project', selectedProduct?: { id: string; name: string }) => void
}

export function ChooseExistingProject({ handleChooseExistingProject, onStageChange }: ChooseExistingProjectProps) {
  const { products } = useContext(DashboardContext)
  const [selectedProduct, setSelectedProduct] = useState<string | null>(null)
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedPlatform, setSelectedPlatform] = useState('')
  const [selectedStatus, setSelectedStatus] = useState('')

  // Notify parent component of stage change
  useEffect(() => {
    if (onStageChange) {
      if (selectedProduct) {
        const product = products.find((p) => p.id === selectedProduct)
        onStageChange('project', product ? { id: product.id, name: product.name } : undefined)
      } else {
        onStageChange('product')
      }
    }
  }, [selectedProduct, onStageChange, products])

  const getPlatformIcon = (platform: string, size = 16, className = 'text-gray-700') => {
    switch (platform) {
      case 'iOS':
        return <Apple size={size} className={className} strokeWidth={2} />
      case 'iPad OS':
        return <Tablet size={size} className={className} strokeWidth={2} />
      case 'Android':
        return <Smartphone size={size} className={className} strokeWidth={2} />
      case 'Web':
        return <Globe size={size} className={className} strokeWidth={2} />
      case 'Mac OS':
        return <Laptop size={size} className={className} strokeWidth={2} />
      case 'Windows':
        return <Computer size={size} className={className} strokeWidth={2} />
      case 'Linux':
        return <Computer size={size} className={className} strokeWidth={2} />
      case 'Multi-Platform':
        return <MonitorSmartphone size={size} className={className} strokeWidth={2} />
      default:
        return <Globe size={size} className={className} strokeWidth={2} />
    }
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Active':
      case 'Live':
        return 'text-[#B2C920]'
      case 'Beta':
        return 'text-[#964EB2]'
      case 'Alpha':
        return 'text-[#CF3F75]'
      case 'Planning':
        return 'text-[#2C8FB1]'
      case 'Ideation':
        return 'text-[#DCAE1D]'
      default:
        return 'text-gray-500'
    }
  }

  const getStatusBadgeClasses = (status: string) => {
    switch (status) {
      case 'Active':
      case 'Live':
        return 'bg-[#B2C920]/20 text-[#B2C920] border border-[#B2C920]/30'
      case 'Beta':
        return 'bg-[#964EB2]/20 text-[#964EB2] border border-[#964EB2]/30'
      case 'Alpha':
        return 'bg-[#CF3F75]/20 text-[#CF3F75] border border-[#CF3F75]/30'
      case 'Planning':
        return 'bg-[#2C8FB1]/20 text-[#2C8FB1] border border-[#2C8FB1]/30'
      case 'Ideation':
        return 'bg-[#DCAE1D]/20 text-[#DCAE1D] border border-[#DCAE1D]/30'
      default:
        return 'bg-gray-100 text-gray-800'
    }
  }

  // Filter projects based on selected product
  const filteredProjects = selectedProduct ? products.find((p) => p.id === selectedProduct)?.projectsData || [] : []

  // Filter products based on search and filters
  const filteredProducts = products.filter((product) => {
    const matchesSearch =
      product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      product.tagline?.toLowerCase().includes(searchTerm.toLowerCase())
    const matchesPlatform = !selectedPlatform || product.platform === selectedPlatform
    const matchesStatus = !selectedStatus || product.status === selectedStatus
    return matchesSearch && matchesPlatform && matchesStatus
  })

  if (selectedProduct) {
    return (
      <div className="flex flex-col gap-4">
        <button
          onClick={() => setSelectedProduct(null)}
          className="flex items-center gap-2 text-[#62708d] hover:text-[#3f424a]"
        >
          <ChevronLeft size={20} />
          <span>Back to Products</span>
        </button>
        <div className="max-h-[400px] overflow-y-auto">
          {filteredProjects.map((project) => (
            <div
              key={project.id}
              className="flex items-center justify-between p-4 border-b border-[#e4e4e4] hover:bg-gray-50"
            >
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center">
                  <ProjectSmallIcon disabled={false} />
                </div>
                <div>
                  <p className="text-[#3f424a] font-semibold">{project.name}</p>
                  <p className="text-[#62708d] text-sm">
                    {project.needs_count || 0} notes • {project.categories_count} categories • {project.meetings_count}{' '}
                    sessions
                  </p>
                </div>
              </div>
              <button
                onClick={() => handleChooseExistingProject(project)}
                className="px-4 py-2 bg-[#FF4A8E] text-white rounded-full hover:bg-[#FF4A8E]/90"
              >
                Select
              </button>
            </div>
          ))}
        </div>
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-6 bg-gray-50/50 dark:bg-gray-900/50">
      <div className="flex items-center gap-4">
        <div className="relative flex-1">
          <input
            type="text"
            placeholder="Search products..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full h-10 pl-10 pr-4 rounded-lg border border-[#e4e4e4] focus:outline-none focus:ring-2 focus:ring-[#FF4A8E]/20 focus:border-[#FF4A8E]"
          />
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} />
        </div>
        <div className="flex gap-2">
          <select
            value={selectedPlatform}
            onChange={(e) => setSelectedPlatform(e.target.value)}
            className="h-10 px-4 rounded-lg border border-[#e4e4e4] focus:outline-none focus:ring-2 focus:ring-[#FF4A8E]/20 focus:border-[#FF4A8E] text-sm"
          >
            <option value="">All Platforms</option>
            <option value="iOS">iOS</option>
            <option value="Android">Android</option>
            <option value="Web">Web</option>
            <option value="Mac OS">Mac OS</option>
            <option value="Windows">Windows</option>
            <option value="Linux">Linux</option>
            <option value="Multi-Platform">Multi-Platform</option>
          </select>
          <select
            value={selectedStatus}
            onChange={(e) => setSelectedStatus(e.target.value)}
            className="h-10 px-4 rounded-lg border border-[#e4e4e4] focus:outline-none focus:ring-2 focus:ring-[#FF4A8E]/20 focus:border-[#FF4A8E] text-sm"
          >
            <option value="">All Status</option>
            <option value="Live">Live</option>
            <option value="Active">Active</option>
            <option value="Beta">Beta</option>
            <option value="Alpha">Alpha</option>
            <option value="Planning">Planning</option>
            <option value="Ideation">Ideation</option>
          </select>
        </div>
      </div>

      {filteredProducts.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-12 text-center">
          <div className="w-24 h-24 bg-[#FF4A8E]/5 rounded-full flex items-center justify-center mb-4">
            <Package size={40} className="text-[#FF4A8E]" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">No Products Found</h3>
          <p className="text-sm text-gray-500">Try adjusting your search or filters</p>
        </div>
      ) : (
        <div className="grid grid-cols-3 gap-6 overflow-y-auto max-h-[600px] pr-2 pb-4">
          {filteredProducts.map((product) => (
            <div
              key={product.id}
              onClick={() => setSelectedProduct(product.id)}
              className="group w-[275px] h-[260px] bg-white dark:bg-gray-800 p-6 rounded-xl border border-[#e4e4e4] dark:border-gray-700 flex flex-col gap-2 cursor-pointer transition-all duration-200 hover:shadow-lg hover:border-[#FF4A8E]/20 hover:scale-[1.02]"
            >
              <div className="text-center space-y-1">
                <p className="text-[#3f424a] dark:text-gray-100 text-lg font-semibold font-['Manrope'] leading-[17.50px] truncate">
                  {product.name}
                </p>
                <p className="text-[#62708d] dark:text-gray-400 text-sm font-medium font-['Manrope'] line-clamp-2">
                  {product.tagline}
                </p>
              </div>

              <div className="flex-1 flex justify-center items-center p-4 group-hover:scale-105 transition-transform duration-200">
                {product.productLogo ? (
                  <img
                    src={product.productLogo}
                    alt={`${product.name} Product Logo`}
                    className="w-full h-full object-contain"
                  />
                ) : (
                  <div className="w-20 h-20 bg-gray-50 dark:bg-gray-800 rounded-xl flex items-center justify-center group-hover:bg-[#FF4A8E]/5">
                    {getPlatformIcon(product.platform, 40, getStatusColor(product.status))}
                  </div>
                )}
              </div>

              <div className="flex items-center justify-center gap-2">
                <span
                  className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusBadgeClasses(product.status)}`}
                >
                  {product.status}
                </span>
                <span className="inline-flex items-center gap-1 text-xs text-gray-500">
                  {getPlatformIcon(product.platform, 12)}
                  {product.platform}
                </span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
